// User Identity Management
// Handles anonymous user identification via cookies and localStorage
import { logger } from './logger';

// Unified visitor ID - same key used by closera-loader.js for experiments
// Using cookie + localStorage for resilience
const VISITOR_ID_KEY = "closera_visitor_id";
const SESSION_ID_STORAGE_KEY = "closera_session_id";
const CONSENT_LEVEL_KEY = "closera_consent";
const PREV_SHOPIFY_ID_KEY = "closera_prev_shopify_id";

// Cookie expiry: 1 year
const COOKIE_EXPIRY_DAYS = 365;

// Generate a UUID v4
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Cookie helpers
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() ?? null;
  }
  return null;
}

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  // Use SameSite=Lax for first-party cookie, Secure for HTTPS
  const isSecure = window.location.protocol === "https:";
  const secureFlag = isSecure ? "; Secure" : "";

  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${secureFlag}`;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// Get or create visitor ID (unified with experiment tracking)
// Uses a multi-layer identification strategy:
// 1. First-party cookie (primary)
// 2. localStorage backup (if cookies blocked)
export function getOrCreateUserId(): string {
  // Try cookie first
  let visitorId = getCookie(VISITOR_ID_KEY);

  if (!visitorId) {
    // Try localStorage backup (same key - unified storage)
    visitorId = localStorage.getItem(VISITOR_ID_KEY);
  }

  if (!visitorId) {
    // Generate new ID
    visitorId = generateUUID();
  }

  // Ensure both storage mechanisms are set
  setCookie(VISITOR_ID_KEY, visitorId, COOKIE_EXPIRY_DAYS);
  localStorage.setItem(VISITOR_ID_KEY, visitorId);

  return visitorId;
}

// Get current visitor ID (without creating if doesn't exist)
export function getUserId(): string | null {
  const cookieId = getCookie(VISITOR_ID_KEY);
  if (cookieId) return cookieId;

  const storageId = localStorage.getItem(VISITOR_ID_KEY);
  return storageId;
}

// Get or create session ID (per browser tab/session)
export function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);

  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
  }

  return sessionId;
}

// Get current session ID
export function getSessionId(): string | null {
  return sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
}

// Consent level management
export type ConsentLevel = "essential" | "functional";

export function getConsentLevel(): ConsentLevel {
  const stored = localStorage.getItem(CONSENT_LEVEL_KEY);
  if (stored === "functional" || stored === "essential") {
    return stored;
  }
  return "essential"; // Default to essential only
}

export function setConsentLevel(level: ConsentLevel): void {
  localStorage.setItem(CONSENT_LEVEL_KEY, level);

  // If downgrading to essential, clear personalization data
  if (level === "essential") {
    clearPersonalizationData();
  }
}

export function hasConsentForPersonalization(): boolean {
  return getConsentLevel() === "functional";
}

// Clear all user identification data (GDPR deletion)
export function clearAllUserData(): void {
  deleteCookie(VISITOR_ID_KEY);
  localStorage.removeItem(VISITOR_ID_KEY);
  sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);
  localStorage.removeItem(CONSENT_LEVEL_KEY);
  localStorage.removeItem(PREV_SHOPIFY_ID_KEY);
}

// Reset identity on logout - generates new anonymous ID and clears all session data
// This prevents cross-user data contamination on shared devices
function resetIdentity(): void {
  // Generate new anonymous ID
  const newId = generateUUID();
  setCookie(VISITOR_ID_KEY, newId, COOKIE_EXPIRY_DAYS);
  localStorage.setItem(VISITOR_ID_KEY, newId);

  // Reset session (force new session after logout)
  sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);

  // Clear chat state and messages (prevent logged-in user's chat from persisting)
  sessionStorage.removeItem('closera-widget-state');
  sessionStorage.removeItem('closera-chat-messages');
}

// Clear just personalization-related data (when consent downgraded)
function clearPersonalizationData(): void {
  // The actual preference data is stored server-side
  // This just ensures we don't track further
}

// Detect device type
export function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

// Try to detect Shopify customer ID (for logged-in users)
export function getShopifyCustomerId(): string | null {
  // Various ways Shopify exposes customer ID
  const win = window as Window & {
    __st?: { cid?: string | number };
    ShopifyAnalytics?: {
      meta?: {
        page?: { customerId?: string | number };
      };
    };
    Shopify?: {
      customer?: { id?: string | number };
    };
  };

  // Try Shopify analytics tracking
  if (win.__st?.cid) {
    return String(win.__st.cid);
  }

  // Try ShopifyAnalytics object
  if (win.ShopifyAnalytics?.meta?.page?.customerId) {
    return String(win.ShopifyAnalytics.meta.page.customerId);
  }

  // Try Shopify global customer object
  if (win.Shopify?.customer?.id) {
    return String(win.Shopify.customer.id);
  }

  return null;
}

// Initialize user identity - call on app/widget load
export interface UserIdentity {
  userId: string;
  sessionId: string;
  shopifyCustomerId: string | null;
  consentLevel: ConsentLevel;
  deviceType: "mobile" | "tablet" | "desktop";
  isNewUser: boolean;
}

export function initializeUserIdentity(): UserIdentity {
  const existingUserId = getUserId();
  const isNewUser = !existingUserId;

  // Get current and previous Shopify customer ID for logout detection
  const currentShopifyId = getShopifyCustomerId();
  const prevShopifyId = localStorage.getItem(PREV_SHOPIFY_ID_KEY);

  // Detect logout: was logged in, now logged out
  // (Account switching always involves logout first, so this covers both cases)
  if (prevShopifyId && !currentShopifyId) {
    logger.info('Logout detected - resetting identity');
    resetIdentity();
    localStorage.removeItem(PREV_SHOPIFY_ID_KEY);
  }

  // Store current Shopify ID for next check
  if (currentShopifyId) {
    localStorage.setItem(PREV_SHOPIFY_ID_KEY, currentShopifyId);
  }

  // Now get the (possibly reset) IDs
  const userId = getOrCreateUserId();
  const sessionId = getOrCreateSessionId();
  const consentLevel = getConsentLevel();
  const deviceType = getDeviceType();

  return {
    userId,
    sessionId,
    shopifyCustomerId: currentShopifyId,
    consentLevel,
    deviceType,
    isNewUser,
  };
}
